
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { submitAuditRequest } from '../../lib/supabase';

export default function AuditPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    industry: '',
    monthlyLeads: '',
    challenges: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // 1. Persist the lead (anon Supabase insert — always runs).
      await submitAuditRequest(formData);

      // 2. Enqueue the actual scan job so the prospect gets a real /scan/ link
      //    emailed to them. Endpoint returns 202 with task_id.
      const r = await fetch('/api/audit/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!r.ok) {
        // Lead saved, but scan job didn't enqueue — treat as partial success
        // so the prospect isn't told "we'll email in 10 minutes" when we won't.
        console.error('audit/request enqueue failed', await r.text());
        setSubmitStatus('error');
        return;
      }

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        website: '',
        industry: '',
        monthlyLeads: '',
        challenges: ''
      });
    } catch (error) {
      console.error('Audit submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white grid-bg pt-36 pb-20">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(13,148,136,0.14), transparent 58%), radial-gradient(ellipse at 85% 20%, rgba(249,115,22,0.10), transparent 55%)' }}
        ></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center flex flex-col items-center">
            <div className="eyebrow"><span>Free AI Check</span></div>
            <h1 className="mt-6 h-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.02em]">
              See What Your Site Misses
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-600 font-body font-light max-w-2xl">
              Get a free check. We will show what is hard to find, where leads leak, and what to fix first.
            </p>
          </div>
        </div>
      </section>

      {/* What You'll Get */}
      <section className="py-16 bg-navy-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-900 mb-12 text-center">
              What's Included in Your Free Assessment
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  icon: 'ri-search-line',
                  title: 'AI Visibility Score',
                  description: 'See if AI tools can find and recommend your business'
                },
                {
                  icon: 'ri-line-chart-line',
                  title: 'Competitive Analysis',
                  description: 'See who shows up first and why'
                },
                {
                  icon: 'ri-file-list-line',
                  title: 'GEO Gap Analysis',
                  description: 'Find missing schema, weak pages, and easy fixes'
                },
                {
                  icon: 'ri-funnel-line',
                  title: 'Lead Funnel Review',
                  description: 'Find where calls and leads slip away'
                },
                {
                  icon: 'ri-calculator-line',
                  title: 'Voice AI ROI Projection',
                  description: 'See what missed calls may be costing you'
                },
                {
                  icon: 'ri-roadmap-line',
                  title: 'Custom Action Plan',
                  description: 'Get a short plan with what to fix first'
                }
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 border-2 border-navy-100 hover:border-teal-300 transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-xl text-teal-600`}></i>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-navy-900 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-base text-navy-600">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-900 mb-4">
                Request Your Free Check
              </h2>
              <p className="text-lg text-navy-600">
                Fill this out. We will send your check within 48 hours.
              </p>
            </div>

            <form 
              id="audit-form"
              data-readdy-form
              onSubmit={handleSubmit} 
              className="bg-navy-50 rounded-2xl p-8 border-2 border-navy-100"
            >
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-base font-semibold text-navy-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-base border border-navy-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-base font-semibold text-navy-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-base border border-navy-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none"
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="phone" className="block text-base font-semibold text-navy-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-base border border-navy-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-base font-semibold text-navy-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-base border border-navy-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none"
                    placeholder="ABC Services"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="website" className="block text-base font-semibold text-navy-700 mb-2">
                  Website URL
                </label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-base border border-navy-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none"
                  placeholder="https://www.yourwebsite.com"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="industry" className="block text-base font-semibold text-navy-700 mb-2">
                    Industry *
                  </label>
                  <select
                    id="industry"
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-base border border-navy-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none"
                  >
                    <option value="">Select your industry</option>
                    <option value="Home Services">Home Services (HVAC, Plumbing, etc.)</option>
                    <option value="Professional Services">Professional Services (Legal, Accounting)</option>
                    <option value="Healthcare">Healthcare & Wellness</option>
                    <option value="Real Estate">Real Estate</option>
                    <option value="Automotive">Automotive</option>
                    <option value="Retail">Retail & E-commerce</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="monthlyLeads" className="block text-base font-semibold text-navy-700 mb-2">
                    Monthly Leads *
                  </label>
                  <select
                    id="monthlyLeads"
                    name="monthlyLeads"
                    value={formData.monthlyLeads}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-base border border-navy-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none"
                  >
                    <option value="">Select range</option>
                    <option value="0-50">0-50 leads/month</option>
                    <option value="51-100">51-100 leads/month</option>
                    <option value="101-250">101-250 leads/month</option>
                    <option value="250+">250+ leads/month</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="challenges" className="block text-base font-semibold text-navy-700 mb-2">
                  Biggest Marketing Challenge
                </label>
                <textarea
                  id="challenges"
                  name="challenges"
                  value={formData.challenges}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 text-base border border-navy-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none resize-none"
                  placeholder="Tell us about your biggest marketing challenge or goal..."
                ></textarea>
                <p className="text-sm text-navy-500 mt-2">
                  {formData.challenges.length}/500 characters
                </p>
              </div>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-navy-50 border-2 border-teal-500 rounded-lg">
                  <p className="text-base text-navy-900 font-semibold">
                    ✓ Your scan is running. We'll email your personalized scan link in about 10 minutes.
                  </p>
                  <p className="text-sm text-navy-600 mt-1">
                    Check your inbox (and spam folder) — the email will come from garfield@floridaaimarketingexperts.com with your AI visibility score, top issues, and revenue gap.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border-2 border-red-600 rounded-lg">
                  <p className="text-base text-red-800 font-semibold">
                    There was an error submitting your request. Please try again or call us directly.
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary px-8 py-4 text-lg disabled:opacity-50 whitespace-nowrap"
              >
                {isSubmitting ? 'Submitting...' : 'Get My Free Assessment'}
              </button>

              <p className="text-sm text-navy-500 text-center mt-4">
                We respect your privacy. Your information will never be shared.
              </p>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
